import React, {
  forwardRef, useEffect, useMemo, useState,
} from 'react';

import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';
import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import validate from '@vlabs/shared/validators';
import { Input } from '@vlabs/uikit/components/controls/input';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import { useWorkspaceFilters } from '../../useWorkspaceFilters';
import st from '../FilterPopovers.module.sass';

const MIN_SIMILARITY = 0;
const MAX_SIMILARITY = 100;

function getValidationError(value: string, min: number): string | undefined {
  const numberValidation = validate.number()(value);
  if (numberValidation !== true) return String(numberValidation);
  if (value === '') return undefined;

  const numberValue = Number(value);
  if (numberValue < min) return String(validate.gte(min).message);
  if (numberValue > MAX_SIMILARITY) return String(validate.lte(MAX_SIMILARITY).message);
  return undefined;
}

function SimilarityFilterPopover(
  props: PopoverWithButtonProps,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { t } = useTranslation();
  const { filters, updateFilters } = useWorkspaceFilters();
  const storedGte = String(filters.top_similar_object_similarity__gte ?? '');
  const storedLt = String(filters.top_similar_object_similarity__lt ?? '');
  const [gte, setGte] = useState(storedGte);
  const [lt, setLt] = useState(storedLt);
  const gteError = getValidationError(gte, MIN_SIMILARITY);
  const ltError = getValidationError(lt, Math.max(Number(gte) || 0, MIN_SIMILARITY));

  useEffect(() => setGte(storedGte), [storedGte]);
  useEffect(() => setLt(storedLt), [storedLt]);

  const updateSimilarity = useMemo(() => debounce((nextFilters: {
    top_similar_object_similarity__gte?: string;
    top_similar_object_similarity__lt?: string;
  }) => updateFilters(nextFilters), 500), [updateFilters]);

  useEffect(() => () => updateSimilarity.cancel(), [updateSimilarity]);

  const handleGteChange = (value: string) => {
    setGte(value);
    if (getValidationError(value, MIN_SIMILARITY) !== undefined) return;
    if (getValidationError(lt, Math.max(Number(value) || 0, MIN_SIMILARITY)) !== undefined) return;
    updateSimilarity({
      top_similar_object_similarity__gte: value || undefined,
      top_similar_object_similarity__lt: lt || undefined,
    });
  };

  const handleLtChange = (value: string) => {
    setLt(value);
    if (getValidationError(gte, MIN_SIMILARITY) !== undefined) return;
    if (getValidationError(value, Math.max(Number(gte) || 0, MIN_SIMILARITY)) !== undefined) return;
    updateSimilarity({
      top_similar_object_similarity__gte: gte || undefined,
      top_similar_object_similarity__lt: value || undefined,
    });
  };

  const reset = () => {
    updateSimilarity.cancel();
    setGte('');
    setLt('');
    updateFilters({
      top_similar_object_similarity__gte: undefined,
      top_similar_object_similarity__lt: undefined,
    });
  };

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={!gte && !lt}
      onClickReset={reset}
      ref={ref}
    >
      <div className={st.InputLabel}>
        {t('similarity %')}
      </div>
      <div className={st.DoubleInputs}>
        <div className={st.Input}>
          <Input
            errorMessage={gteError}
            hasError={gteError !== undefined}
            id="top_similar_object_similarity__gte"
            isClearable
            onChangeValue={handleGteChange}
            onClear={() => handleGteChange('')}
            placeholder={t('от')}
            value={gte}
          />
        </div>
        <div className={st.Divider}>:</div>
        <div className={st.Input}>
          <Input
            errorMessage={ltError}
            hasError={ltError !== undefined}
            id="top_similar_object_similarity__lt"
            isClearable
            onChangeValue={handleLtChange}
            onClear={() => handleLtChange('')}
            placeholder={t('до')}
            value={lt}
          />
        </div>
      </div>
    </PopoverWithButton>
  );
}

const SimilarityFilterPopoverWithRef = forwardRef(SimilarityFilterPopover);
export { SimilarityFilterPopoverWithRef as SimilarityFilterPopover };
